import { ChevronLeft } from "lucide-react";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import CookieConsent from "@/components/CookieConsent";
import SEOHead from "@/components/SEOHead";

const Privacy = () => {
  return (
    <div className="min-h-screen bg-background flex flex-col">
      <SEOHead
        title="Privacy Policy | MySabbat"
        description="Read the MySabbat Privacy Policy. We protect your personal data in accordance with GDPR."
        path="/privacy"
      />
      <Header />

      <main className="flex-1 py-12 md:py-20">
        <div className="container">
          <div className="max-w-2xl mx-auto mb-8">
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary/10 text-primary hover:bg-primary/20 transition-colors font-medium text-sm"
            >
              <ChevronLeft className="w-4 h-4" />
              Back to home
            </Link>
          </div>

          <article className="max-w-2xl mx-auto">
            <h1 className="text-3xl md:text-4xl font-semibold text-foreground mb-2">
              Privacy Policy – MySabbat
            </h1>
            <p className="text-muted-foreground mb-10">Last updated: March 2026</p>

            <div className="bg-card rounded-2xl p-8 md:p-10 shadow-sm border border-border/40 space-y-8">
              <section>
                <h2 className="text-xl font-semibold text-foreground mb-4">1. Introduction</h2>
                <p className="text-foreground/80 leading-relaxed">
                  MySabbat respects your privacy and is committed to protecting your personal data. This Privacy Policy explains how we collect, use, and protect your information when you use the MySabbat app.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-foreground mb-4">2. Information We Collect</h2>

                <h3 className="text-lg font-medium text-foreground mb-2">Account Information</h3>
                <p className="text-foreground/80 leading-relaxed mb-2">When you create an account, we may collect:</p>
                <ul className="list-disc pl-6 space-y-1 text-foreground/80 mb-4">
                  <li>Name</li>
                  <li>Email address</li>
                  <li>Age or birth year</li>
                  <li>Profile information you choose to provide</li>
                </ul>

                <h3 className="text-lg font-medium text-foreground mb-2">App Usage Data</h3>
                <p className="text-foreground/80 leading-relaxed mb-2">We may collect information about how you use the app, such as:</p>
                <ul className="list-disc pl-6 space-y-1 text-foreground/80 mb-4">
                  <li>Features you interact with</li>
                  <li>Goals and preferences you enter</li>
                  <li>App performance and crash reports</li>
                </ul>

                <h3 className="text-lg font-medium text-foreground mb-2">Device Information</h3>
                <p className="text-foreground/80 leading-relaxed mb-2">We may collect limited technical information such as:</p>
                <ul className="list-disc pl-6 space-y-1 text-foreground/80">
                  <li>Device type</li>
                  <li>Operating system</li>
                  <li>App version</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-foreground mb-4">3. How We Use Your Information</h2>
                <p className="text-foreground/80 leading-relaxed mb-2">We use your information to:</p>
                <ul className="list-disc pl-6 space-y-1 text-foreground/80">
                  <li>Provide and operate the MySabbat app</li>
                  <li>Personalize recommendations and content</li>
                  <li>Improve app functionality</li>
                  <li>Provide customer support</li>
                  <li>Ensure security and prevent misuse</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-foreground mb-4">4. AI Services</h2>
                <p className="text-foreground/80 leading-relaxed">
                  Some features in MySabbat may use AI services to generate responses or suggestions. Information you provide in chats or inputs may be processed by AI systems to generate responses.
                </p>
                <p className="text-foreground/80 leading-relaxed mt-3">
                  We only share the information necessary for these services to function.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-foreground mb-4">5. Data Storage and Security</h2>
                <p className="text-foreground/80 leading-relaxed">
                  We use secure systems to store and protect your information. Reasonable technical and organizational measures are taken to prevent unauthorized access, loss, or misuse of data.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-foreground mb-4">6. Data Sharing</h2>
                <p className="text-foreground/80 leading-relaxed mb-3">We do not sell your personal data.</p>
                <p className="text-foreground/80 leading-relaxed mb-2">We may share data with:</p>
                <ul className="list-disc pl-6 space-y-1 text-foreground/80">
                  <li>Technical service providers (hosting, analytics, AI services)</li>
                  <li>Payment providers (for subscriptions)</li>
                  <li>Legal authorities if required by law</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-foreground mb-4">7. Your Rights (GDPR)</h2>
                <p className="text-foreground/80 leading-relaxed mb-2">If you are located in the EU, you have the right to:</p>
                <ul className="list-disc pl-6 space-y-1 text-foreground/80 mb-3">
                  <li>Access your personal data</li>
                  <li>Correct inaccurate data</li>
                  <li>Request deletion of your data</li>
                  <li>Withdraw consent</li>
                  <li>Request data portability</li>
                </ul>
                <p className="text-foreground/80 leading-relaxed">You can contact us to exercise these rights.</p>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-foreground mb-4">8. Data Retention</h2>
                <p className="text-foreground/80 leading-relaxed">
                  We keep your personal data only as long as necessary to provide the service or comply with legal obligations.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-foreground mb-4">9. Children's Privacy</h2>
                <p className="text-foreground/80 leading-relaxed">
                  MySabbat is not intended for children under 13. We do not knowingly collect personal data from children under this age.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-foreground mb-4">10. Changes to This Policy</h2>
                <p className="text-foreground/80 leading-relaxed">
                  We may update this Privacy Policy from time to time. Updates will be published in the app.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-foreground mb-4">11. Contact</h2>
                <p className="text-foreground/80 leading-relaxed">
                  If you have questions about this Privacy Policy or your data, contact us at:{" "}
                  <a href="mailto:kontakt@mysabbat.dk" className="text-primary hover:underline">
                    kontakt@mysabbat.dk
                  </a>
                </p>
              </section>
            </div>
          </article>
        </div>
      </main>

      <Footer />
      <CookieConsent />
    </div>
  );
};

export default Privacy;
